import { Button, Dialog, Field, Notice, Panel, PanelHead, useToast } from '@concord/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { ZONES } from './InviteAccount';

interface Profile {
  firstName: string;
  lastName: string;
  displayName: string | null;
  email: string;
  phone: string | null;
  mobile: string | null;
  city: string | null;
  country: string;
  timezone: string;
  role: string;
  permissions: string[];
  grantable: { key: string; label: string }[];
}

type Form = Record<
  | 'firstName'
  | 'lastName'
  | 'displayName'
  | 'email'
  | 'phone'
  | 'mobile'
  | 'city'
  | 'country'
  | 'timezone'
  | 'role',
  string
>;

const profileKey = (id: string) => ['account-profile', id];

function useProfile(id: string, enabled: boolean) {
  return useQuery({
    queryKey: profileKey(id),
    queryFn: () => api<Profile>(`/staff/directory/account/${id}/profile`),
    enabled,
  });
}

const toForm = (p: Profile): Form => ({
  firstName: p.firstName,
  lastName: p.lastName,
  displayName: p.displayName ?? '',
  email: p.email,
  phone: p.phone ?? '',
  mobile: p.mobile ?? '',
  city: p.city ?? '',
  country: p.country,
  timezone: p.timezone,
  role: p.role,
});

/**
 * Super admin: edit anyone's name, email, phone numbers, location, time zone and role
 * (patient or care navigator). Only changed fields are sent, so the audit trail names
 * exactly what changed. An email or role change signs the person out everywhere.
 */
export function EditAccountDialog({
  accountId,
  self,
  open,
  onOpenChange,
}: {
  accountId: string;
  self: boolean;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const p = useProfile(accountId, open);
  const [form, setForm] = useState<Form | null>(null);
  useEffect(() => {
    if (open && p.data) setForm(toForm(p.data));
    if (!open) setForm(null);
  }, [open, p.data]);
  const save = useMutation({
    mutationFn: (patch: Record<string, string | null>) =>
      api(`/staff/directory/account/${accountId}/profile`, { method: 'PATCH', body: patch }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['account', accountId] });
      await qc.invalidateQueries({ queryKey: profileKey(accountId) });
      await qc.invalidateQueries({ queryKey: ['me'] });
      onOpenChange(false);
      toast('Account saved.');
    },
  });
  const set = (k: keyof Form, v: string) => setForm((f) => (f ? { ...f, [k]: v } : f));
  const orig = p.data ? toForm(p.data) : null;
  const changed =
    form && orig ? (Object.keys(form) as (keyof Form)[]).filter((k) => form[k] !== orig[k]) : [];
  const signsOut = changed.includes('email') || changed.includes('role');
  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    if (!form || !changed.length) return;
    const nullable = new Set(['displayName', 'phone', 'mobile', 'city']);
    save.mutate(
      Object.fromEntries(changed.map((k) => [k, nullable.has(k) && !form[k].trim() ? null : form[k]])),
    );
  };
  const text = (k: keyof Form, label: string, hint?: string, type = 'text') => (
    <Field label={label} hint={hint}>
      {(id, d) => (
        <input
          id={id}
          type={type}
          className="input"
          aria-describedby={d}
          value={form?.[k] ?? ''}
          onChange={(e) => set(k, e.target.value)}
        />
      )}
    </Field>
  );
  const zones = form && !ZONES.includes(form.timezone) ? [form.timezone, ...ZONES] : ZONES;
  const roleLocked = self || !['patient', 'navigator'].includes(orig?.role ?? '');
  const grid = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.9rem' } as const;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Edit account"
      description="Super admin. Every change is recorded in the audit log."
      footer={
        <>
          <Button variant="sec" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button busy={save.isPending} disabled={!changed.length} onClick={() => submit()}>
            Save changes
          </Button>
        </>
      }
    >
      {p.isError && <Notice tone="bad">{errorMessage(p.error)}</Notice>}
      {form && (
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: '.9rem' }}>
          <div style={grid}>
            {text('firstName', 'First name')}
            {text('lastName', 'Last name')}
          </div>
          {text('displayName', 'Display name', 'Optional. Shown instead of the full name.')}
          {text(
            'email',
            'Email address',
            'Their sign-in address. Both addresses are told of a change.',
            'email',
          )}
          <div style={grid}>
            {text('phone', 'Phone')}
            {text('mobile', 'Mobile')}
          </div>
          <div style={grid}>
            {text('city', 'City')}
            {text('country', 'Country', 'Two-letter code, such as US.')}
          </div>
          <div style={grid}>
            <Field label="Time zone">
              {(id) => (
                <select
                  id={id}
                  className="select"
                  value={form.timezone}
                  onChange={(e) => set('timezone', e.target.value)}
                >
                  {zones.map((z) => (
                    <option key={z} value={z}>
                      {z.replace('America/', '').replace('_', ' ')}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <Field
              label="Role"
              hint={
                self
                  ? 'You cannot change your own role.'
                  : roleLocked
                    ? 'Physician accounts keep their role.'
                    : 'Starts from the new role’s default permissions.'
              }
            >
              {(id, d) => (
                <select
                  id={id}
                  className="select"
                  aria-describedby={d}
                  value={form.role}
                  disabled={roleLocked}
                  onChange={(e) => set('role', e.target.value)}
                >
                  {roleLocked && !['patient', 'navigator'].includes(form.role) && (
                    <option value={form.role}>Physician</option>
                  )}
                  <option value="patient">Patient</option>
                  <option value="navigator">Care Navigator</option>
                </select>
              )}
            </Field>
          </div>
          {signsOut && (
            <Notice>
              Changing the {changed.includes('email') ? 'email' : 'role'} signs this person out everywhere.
            </Notice>
          )}
          {save.isError && <Notice tone="bad">{errorMessage(save.error)}</Notice>}
        </form>
      )}
    </Dialog>
  );
}

/** Super admin: a care navigator's console permissions, including admin and super admin. */
export function PermissionsPanel({ accountId, self }: { accountId: string; self: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const p = useProfile(accountId, true);
  const [want, setWant] = useState<string[] | null>(null);
  const current = want ?? p.data?.permissions ?? [];
  const dirty = want !== null && [...want].sort().join() !== [...(p.data?.permissions ?? [])].sort().join();
  const save = useMutation({
    mutationFn: () =>
      api(`/staff/directory/account/${accountId}/permissions`, {
        method: 'PUT',
        body: { permissions: current },
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: profileKey(accountId) });
      await qc.invalidateQueries({ queryKey: ['account', accountId] });
      setWant(null);
      toast('Permissions saved.');
    },
    onError: (e) => toast(errorMessage(e), 'bad'),
  });
  const toggle = (k: string) =>
    setWant(current.includes(k) ? current.filter((x) => x !== k) : [...current, k]);
  return (
    <Panel aria-label="Permissions">
      <PanelHead title="Permissions" sub="Super admin">
        {dirty && (
          <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: '.5rem' }}>
            <Button variant="ghost" onClick={() => setWant(null)}>
              Undo
            </Button>
            <Button busy={save.isPending} onClick={() => save.mutate()}>
              Save permissions
            </Button>
          </span>
        )}
      </PanelHead>
      <div className="pbody" style={{ display: 'flex', flexDirection: 'column', gap: '.55rem' }}>
        {p.isError && <Notice tone="bad">{errorMessage(p.error)}</Notice>}
        {p.data?.grantable.map((g) => {
          const locked = self && g.key === 'admin.super';
          return (
            <label key={g.key} style={{ display: 'flex', gap: '.55rem', alignItems: 'flex-start' }}>
              <input
                type="checkbox"
                checked={current.includes(g.key)}
                disabled={locked}
                onChange={() => toggle(g.key)}
              />
              <span>
                {g.label}
                <span className="sub2"> · {g.key}</span>
                {locked && <div className="sub2">You cannot remove your own super admin access.</div>}
              </span>
            </label>
          );
        })}
      </div>
    </Panel>
  );
}
